import { CircleAlert, LoaderCircle } from "lucide-react";
import { cloneElement, createContext, isValidElement, useCallback, useContext, useEffect, useId, useState, type InputHTMLAttributes, type KeyboardEvent, type ReactNode } from "react";
import { iniciais } from "../lib/formato";
import { useProduto } from "../lib/produto";

/** Aviso que vale para todas as telas (WhatsApp desconectado), mostrado logo abaixo do cabeçalho. */
export const FaixaGlobal = createContext<ReactNode>(null);

/**
 * Cabeçalho da página (modelo v6): título de 24 px, uma linha de descrição e as ações à direita.
 * Não fica preso no alto: quem fica é a barra superior da casca, com a trilha e a busca.
 */
export function Cabecalho({
  titulo,
  tituloDaAba,
  subtitulo,
  voltar,
  acoes,
}: {
  titulo: string;
  /** Título da aba do navegador quando o da tela tem dado pessoal (fica no histórico do aparelho). */
  tituloDaAba?: string;
  subtitulo?: ReactNode;
  voltar?: ReactNode;
  acoes?: ReactNode;
}) {
  const faixa = useContext(FaixaGlobal);
  const produto = useProduto();
  useEffect(() => {
    document.title = produto ? `${tituloDaAba ?? titulo} · ${produto}` : (tituloDaAba ?? titulo);
  }, [titulo, tituloDaAba, produto]);
  return (
    <>
      <header className="flex flex-wrap items-center gap-x-4 gap-y-3 px-4 pt-4 md:px-8 md:pt-5 xl:px-10">
        {voltar}
        <div className="min-w-[min(100%,16rem)] flex-1">
          {/* No celular, até 2 linhas: um nome longo não invade os botões ao lado. */}
          <h1 title={titulo} className="line-clamp-2 text-[22px] leading-8 font-semibold break-words md:text-2xl">
            {titulo}
          </h1>
          {subtitulo && <div className="mt-0.5 text-sm text-muted-foreground">{subtitulo}</div>}
        </div>
        {acoes && <div className="flex flex-wrap items-center gap-2">{acoes}</div>}
      </header>
      {faixa && <div className="px-4 pt-3 md:px-8 xl:px-10">{faixa}</div>}
    </>
  );
}

/**
 * Área da página: ocupa a largura toda da janela. `estreito` (formulários e listas de configuração) só limita em
 * monitores largos (1152 px), alinhado à esquerda junto do título, e não centralizado numa caixa.
 */
export function Conteudo({ children, estreito }: { children: ReactNode; estreito?: boolean; largo?: boolean }) {
  return <div className={`w-full px-4 pt-4 pb-10 md:px-8 xl:px-10 ${estreito ? "max-w-6xl" : ""}`}>{children}</div>;
}

/** O que o campo precisa para ficar ligado ao rótulo, à ajuda e ao erro. */
export type PropsControle = { id: string; "aria-invalid"?: boolean; "aria-describedby"?: string };

/**
 * Rótulo, campo, ajuda e erro de um formulário: o único jeito de montar um campo na aplicação.
 * O filho pode ser o próprio controle (fica dentro do rótulo e recebe aria-invalid e aria-describedby)
 * ou uma função que recebe essas props, para quando o controle não é o primeiro elemento.
 */
export function Campo({
  rotulo,
  erro,
  ajuda,
  obrigatorio,
  compacto,
  children,
}: {
  rotulo: string;
  erro?: string;
  ajuda?: string;
  obrigatorio?: boolean;
  /** Rótulo menor, para grades densas (itens de nota, parcelas). */
  compacto?: boolean;
  children: ReactNode | ((p: PropsControle) => ReactNode);
}) {
  const id = useId();
  const descricao = erro ? `${id}-erro` : ajuda ? `${id}-ajuda` : undefined;
  const aria = { "aria-describedby": descricao, "aria-invalid": erro ? true : undefined };
  const textoRotulo = (
    <>
      {rotulo}
      {obrigatorio && <span className="text-perigo"> *</span>}
    </>
  );
  const classeRotulo = compacto ? "mb-1 block text-xs font-medium text-texto-suave" : "mb-1.5 block text-sm font-medium";
  return (
    <div className="min-w-0">
      {typeof children === "function" ? (
        <>
          <label htmlFor={id} className={classeRotulo}>
            {textoRotulo}
          </label>
          {children({ id, ...aria })}
        </>
      ) : (
        // Ajuda e erro ficam fora do rótulo (não entram no nome do campo) e ligados a ele por aria-describedby.
        <label className="block">
          <span className={classeRotulo}>{textoRotulo}</span>
          {isValidElement<typeof aria>(children) ? cloneElement(children, aria) : children}
        </label>
      )}
      {ajuda && !erro && (
        <p id={`${id}-ajuda`} className="mt-1 text-xs text-texto-suave">
          {ajuda}
        </p>
      )}
      {erro && (
        <p id={`${id}-erro`} className="mt-1 text-[13px] text-perigo">
          {erro}
        </p>
      )}
    </div>
  );
}

/** Setas trocam a opção de um grupo de rádios (padrão ARIA): o foco e a escolha andam juntos. */
function teclasRadio(e: KeyboardEvent<HTMLElement>) {
  const passo = ({ ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 } as Record<string, number>)[e.key];
  if (!passo) return;
  const radios = [...e.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')];
  const atual = radios.indexOf(document.activeElement as HTMLElement);
  if (atual < 0) return;
  e.preventDefault();
  const proximo = radios[(atual + passo + radios.length) % radios.length]!;
  proximo.click();
  proximo.focus();
}

/** Opções exclusivas lado a lado, com um só ponto de Tab e setas para trocar. */
export function GrupoOpcoes<T extends string>({
  rotulo,
  opcoes,
  valor,
  aoEscolher,
  className,
  classeOpcao = () => "",
  permitirNenhuma,
}: {
  rotulo: string;
  opcoes: { valor: T; texto: string }[];
  valor: T | null;
  aoEscolher: (v: T | null) => void;
  className?: string;
  classeOpcao?: (marcada: boolean) => string;
  /** Tocar de novo na opção marcada desmarca (valor vazio). */
  permitirNenhuma?: boolean;
}) {
  const marcada = Math.max(0, opcoes.findIndex((o) => o.valor === valor));
  return (
    <div role="radiogroup" aria-label={rotulo} className={className} onKeyDown={teclasRadio}>
      {opcoes.map((o, i) => (
        <button
          key={o.valor}
          type="button"
          role="radio"
          aria-checked={o.valor === valor}
          tabIndex={i === marcada ? 0 : -1}
          onClick={() => aoEscolher(permitirNenhuma && o.valor === valor ? null : o.valor)}
          className={classeOpcao(o.valor === valor)}
        >
          {o.texto}
        </button>
      ))}
    </div>
  );
}

/** Texto digitado ("0,5", ",5", "5,") como número; vazio ou só o separador ainda não é número. */
const lerNumero = (t: string) => (/\d/.test(t) ? Number(t.replace(",", ".")) : null);
const mostrarNumero = (n: number) => String(n).replace(".", ",");

/**
 * Campo numérico que guarda o texto digitado: a vírgula, o zero à esquerda e o campo vazio não somem
 * no meio da digitação. Informa o número a cada tecla e reescreve o texto ao sair do campo.
 */
export function EntradaNumero({
  valor,
  aoMudar,
  casas = 0,
  onBlur,
  ...resto
}: { valor: number; aoMudar: (n: number) => void; casas?: number } & Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "inputMode">) {
  const [texto, setTexto] = useState(() => mostrarNumero(valor));
  // Valor trocado de fora (outro ponto selecionado, dados recarregados) aparece no campo.
  useEffect(() => {
    setTexto((t) => (lerNumero(t) === valor ? t : mostrarNumero(valor)));
  }, [valor]);
  const aceito = casas > 0 ? new RegExp(`^\\d*([.,]\\d{0,${casas}})?$`) : /^\d*$/;
  return (
    <input
      {...resto}
      inputMode={casas > 0 ? "decimal" : "numeric"}
      value={texto}
      onChange={(e) => {
        const t = e.target.value.trim();
        if (!aceito.test(t)) return;
        setTexto(t);
        const n = lerNumero(t);
        if (n !== null) aoMudar(n);
      }}
      onBlur={(e) => {
        setTexto(mostrarNumero(valor));
        onBlur?.(e);
      }}
    />
  );
}

export function Avatar({ nome, tamanho = "md" }: { nome: string; tamanho?: "md" | "lg" }) {
  const t = tamanho === "lg" ? "size-16 text-xl" : "size-10 text-sm";
  return (
    <div
      aria-hidden
      className={`${t} grid shrink-0 place-items-center rounded-full bg-marca-suave font-semibold text-marca`}
    >
      {iniciais(nome)}
    </div>
  );
}

export function Carregando({ texto = "Carregando…" }: { texto?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-texto-suave" role="status">
      <LoaderCircle className="size-5 animate-spin" />
      {texto}
    </div>
  );
}

export function FalhaCarregar({ erro, tentarDeNovo }: { erro: Error; tentarDeNovo?: () => void }) {
  return (
    <div className="cartao flex flex-col items-center gap-3 px-6 py-12 text-center" role="alert">
      <CircleAlert className="size-8 text-perigo" />
      <div>
        <div className="font-semibold">Não foi possível carregar</div>
        <div className="text-sm text-texto-suave">{erro.message}</div>
      </div>
      {tentarDeNovo && (
        <button type="button" className="botao botao-secundario" onClick={tentarDeNovo}>
          Tentar de novo
        </button>
      )}
    </div>
  );
}

export function EstadoVazio({ icone, titulo, texto, acao }: { icone: ReactNode; titulo: string; texto?: string; acao?: ReactNode }) {
  return (
    <div className="cartao flex flex-col items-center gap-3 px-6 py-14 text-center">
      <div className="grid size-14 place-items-center rounded-[var(--raio)] bg-muted text-muted-foreground">{icone}</div>
      <div>
        <div className="font-semibold">{titulo}</div>
        {texto && <div className="mx-auto max-w-sm text-sm text-texto-suave">{texto}</div>}
      </div>
      {acao}
    </div>
  );
}

type Aviso = { id: number; texto: string; tipo: "ok" | "erro" };
const ContextoAvisos = createContext<(texto: string, tipo?: Aviso["tipo"]) => void>(() => {});

/** Avisos curtos ("Paciente salvo") no canto de baixo, no estilo do Sonner (shadcn/ui), acima da barra inferior no celular. */
export function ProvedorAvisos({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const avisar = useCallback((texto: string, tipo: Aviso["tipo"] = "ok") => {
    const id = Date.now() + Math.random();
    setAvisos((a) => {
      // Confirmação nova toma o lugar da anterior (em sequência, como no primeiro uso, elas se empilhavam).
      // Erros ficam mais tempo na tela, até dois, sem repetir o mesmo texto.
      if (tipo === "ok") return [...a.filter((x) => x.tipo !== "ok"), { id, texto, tipo }];
      const erros = a.filter((x) => x.tipo === "erro" && x.texto !== texto).slice(-1);
      return [...a.filter((x) => x.tipo === "ok"), ...erros, { id, texto, tipo }];
    });
    setTimeout(() => setAvisos((a) => a.filter((x) => x.id !== id)), tipo === "ok" ? 3500 : 7000);
  }, []);

  return (
    <ContextoAvisos.Provider value={avisar}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-[60] flex flex-col items-center gap-2 px-4 tab:bottom-6">
        {/* Confirmações esperam a vez do leitor de tela; erros são anunciados na hora. */}
        <div aria-live="polite" className="flex flex-col items-center gap-2">
          {avisos
            .filter((a) => a.tipo === "ok")
            .map((a) => (
              <div key={a.id} className="sobreposta rounded-[var(--raio-grande)] border border-border bg-popover px-4 py-3 text-sm font-medium">
                {a.texto}
              </div>
            ))}
        </div>
        <div role="alert" className="flex flex-col items-center gap-2">
          {avisos
            .filter((a) => a.tipo === "erro")
            .map((a) => (
              <div key={a.id} className="sobreposta rounded-[var(--raio-grande)] border border-border bg-popover px-4 py-3 text-sm font-medium text-perigo">
                {a.texto}
              </div>
            ))}
        </div>
      </div>
    </ContextoAvisos.Provider>
  );
}

export const useAvisar = () => useContext(ContextoAvisos);
